import { Box } from '@mui/material';
import { useParams } from 'react-router';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MainLayout } from '../components/MainLayout';
import { CarrouselCenterMode } from '../components/CarrouselCenterMode';
import { UnitCard } from '../components/unitCard';
import { languageState, selectProductById, variantsState } from '../states/state';
import {
  getAddress,
  getAttributeChoice,
  getFloorplans,
  getLink,
  getMainImage,
  getPropertySliderGallery,
  getPropertyUnitGallery,
  getValueByLang,
} from '../utils/products';
import logo from '../logo.png';
import { ILanguage } from '../services/api.services';
import { api } from '../config/config';

// Types for styled-components props
interface ImageProps {
  $full: boolean;
}

// Styled Components with proper type definitions
const PropertyImage = styled.img<ImageProps>`
  width: ${(props): string => (props.$full ? '100%' : '80%')};
  object-fit: cover;
  max-height: 45vw;
  max-width: ${(props): string => (props.$full ? '100%' : '35rem')};
`;

const ImageContainer = styled.div<ImageProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${(props): string => (props.$full ? '0' : '10rem 0')};
  background-color: ${(props): string => props.theme.colors.dark};
`;

const ApplyButton = styled.button`
  background-color: ${(props): string => props.theme.colors.yellow};
  border: 1px solid white;
  color: white;
  padding: 10px;
  cursor: pointer;

  &:hover {
    background-color: white;
    color: ${(props): string => props.theme.colors.yellow};
    border-color: ${(props): string => props.theme.colors.yellow};
    transition: ease 0.5s;
  }
`;

const FloorPlansContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
  a {
    width: 200px;
  }
`;

// Main Component
export const PropertiesDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const lang = useRecoilValue(languageState);
  const variants = useRecoilValue(variantsState);
  const property = useRecoilValue(selectProductById(id ?? ''));

  // Data processing with proper type checking
  const mainImage = getMainImage(property?.gallery);
  const link = getLink(property?.attributes);
  const mainSlider = getPropertySliderGallery(property?.gallery);
  const unitSlider = getPropertyUnitGallery(property?.gallery);
  const floorPlans = getFloorplans(property?.attributes);
  const address = getAddress(property?.attributes);

  const propertyVariants = property?.variants
    ?.map((v) => variants.find((va) => va._id === v.product._id))
    .filter((variant): variant is NonNullable<typeof variant> => Boolean(variant));

  const handleApply = (): void => {
    if (link) {
      window.open(link as string, '_blank', 'noopener,noreferrer');
    }
  };

  const googleMap = address?.name ? (
    <iframe
      title='Property Location'
      width='100%'
      height='450'
      loading='lazy'
      src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API || api.googleMapsApi}&q=${getValueByLang(getValueByLang(address.name, lang) as string, lang) as string}`}
    />
  ) : null;

  return (
    <MainLayout>
      {/* Main Image */}
      <ImageContainer $full={Boolean(mainImage)}>
        <PropertyImage
          $full={Boolean(mainImage)}
          src={mainImage || logo}
          alt={getValueByLang(property?.name, lang) || ''}
        />
      </ImageContainer>

      {/* Property Details */}
      <Box sx={{ padding: { xs: '2rem', md: '4rem' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2>{getValueByLang(property?.name, lang)?.toUpperCase() || ''}</h2>
            {property?.attributes
              .filter((attr) => !/(link|floor-plans|link_pdf)/i.test(attr.attribute.key))
              .map((attr) => (
                <div key={attr.attribute.key} className='mt-2 text-sm text-gray-500'>
                  <span className='font-bold' style={{ marginRight: '.5rem' }}>{getValueByLang(attr.attribute.name, lang)}</span>
                  <span>{getValueByLang((getAttributeChoice(attr) ?? attr).name as unknown as ILanguage, lang) as string}</span>
                </div>
              ))}
          </div>
          {link && <ApplyButton onClick={handleApply}>{t('applyNow')}</ApplyButton>}
        </Box>

        <div
          dangerouslySetInnerHTML={{
            __html: getValueByLang(property?.description, lang) || '',
          }}
        />
      </Box>

      {/* Sliders */}
      {mainSlider?.length > 0 && (
        <CarrouselCenterMode imgs={mainSlider as string[]} autoPlay centerMode={mainSlider.length > 1} />
      )}

      {unitSlider?.length > 0 && (
        <div className='mt-10'>
          <CarrouselCenterMode imgs={unitSlider as string[]} autoPlay />
        </div>
      )}

      {/* Unit Types */}
      {propertyVariants && propertyVariants.length > 0 && (
        <div className='bg-white py-16 px-4'>
          <div className='mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-24 lg:max-w-7xl lg:px-8'>
            <h2 className='text-2xl font-bold mb-6'>{t('unitTypes')}</h2>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
              {propertyVariants.map((unit) => (
                <UnitCard key={unit._id} unit={unit} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Floor Plans */}
      {floorPlans && (
        <FloorPlansContainer>
          <a
            href={floorPlans as string}
            target='_blank'
            rel='noreferrer'
            className='bg-gold text-white text-center py-2 px-3'
          >
            {t('viewAllFloorPlans')}
          </a>
        </FloorPlansContainer>
      )}

      {/* Google Maps */}
      {googleMap}
    </MainLayout>
  );
};
